import { Component, computed, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { GeoArcEditSessionService } from '@core/services/geo-arc-edit-session/geo-arc-edit-session.service';
import { ICONS } from '@core/services/icons/icons.service';
import { ArcEndpoints, pathLengthKm, validatePath } from '@core/utils/geo-polyline.utils';

import { GeoLocation } from '@features/components/visualization/mapbox/models/map-data';

/**
 * An emptied path is a valid result — clearing then committing is how an arc's geometry is
 * removed — so emptiness commits. What blocks the commit is an in-flight request, or a path
 * the backend would reject: one point, or ends further from the arc's sites than the
 * tolerance allows, which a session opened on an already-drifted stored polyline starts in.
 */
export function canCommitPath(
  path: GeoLocation[],
  isComputing: boolean,
  endpoints: ArcEndpoints | null
): boolean {
  if (isComputing) {
    return false;
  }
  return validatePath(path, endpoints) === null;
}

/**
 * The visible boundary of an arc edit session: what the map is waiting for, what has been
 * routed so far, and the two ways out.
 */
@Component({
  selector: 'app-geo-arc-edit-panel',
  imports: [TranslatePipe],
  templateUrl: './geo-arc-edit-panel.component.html'
})
export class GeoArcEditPanelComponent {
  protected readonly ICONS = ICONS;
  protected readonly session = inject(GeoArcEditSessionService);

  protected readonly pointCount = computed(() => this.session.path().length);
  protected readonly lengthKm = computed(
    () => Math.round(pathLengthKm(this.session.path()) * 10) / 10
  );
  protected readonly hasGeometry = computed(() => this.session.path().length > 0);
  protected readonly problem = computed(() =>
    validatePath(this.session.path(), this.session.endpoints())
  );
  protected readonly canCommit = computed(() =>
    canCommitPath(this.session.path(), this.session.isComputing(), this.session.endpoints())
  );

  protected onClear(): void {
    this.session.clear();
  }

  protected onDiscard(): void {
    this.session.cancel();
  }

  protected onDone(): void {
    this.session.commit();
  }
}
